/**
 * RiskMLDetail — Single ticker risk detail page
 *
 * Professional flat design matching UpsideMLDetail.
 * Shows risk probability history, position sizing, prediction table.
 */
import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  Box, Typography, Chip, Alert, Paper, Collapse,
  Table, TableBody, TableCell, TableContainer, TableHead, TableRow,
  IconButton, Tooltip, ToggleButton, ToggleButtonGroup,
  CircularProgress, Button,
} from '@mui/material';
import {
  ArrowBack as BackIcon,
  Refresh as RefreshIcon,
  Shield as ShieldIcon,
  TrendingDown as TrendDownIcon,
  TrendingUp as TrendUpIcon,
  Info as InfoIcon,
  Warning as WarningIcon,
  Speed as SpeedIcon,
  CalendarMonth as CalendarIcon,
  BarChart as BarChartIcon,
  ExpandMore as ExpandMoreIcon,
  ShowChart as ChartIcon,
  Bolt as BoltIcon,
  NotificationsActive as AlertIcon,
} from '@mui/icons-material';
import {
  Area, XAxis, YAxis, CartesianGrid,
  Tooltip as RTooltip, ResponsiveContainer,
  ReferenceLine, ComposedChart, Line,
} from 'recharts';
import { riskMLAPI } from '../services/api';

/* ═══════════════════════════════════════════════════════════════
   DESIGN TOKENS — Matching dashboard-wide palette
   ═══════════════════════════════════════════════════════════════ */

const SEV = {
  0: { key: 'safe',     label: 'SAFE',     bg: '#22c55e', text: '#15803d', light: '#f0fdf4', gradient: 'linear-gradient(135deg, #22c55e, #16a34a)' },
  1: { key: 'caution',  label: 'CAUTION',  bg: '#eab308', text: '#a16207', light: '#fefce8', gradient: 'linear-gradient(135deg, #eab308, #ca8a04)' },
  2: { key: 'warning',  label: 'WARNING',  bg: '#ef4444', text: '#b91c1c', light: '#fef2f2', gradient: 'linear-gradient(135deg, #ef4444, #dc2626)' },
  3: { key: 'critical', label: 'CRITICAL', bg: '#a855f7', text: '#7e22ce', light: '#faf5ff', gradient: 'linear-gradient(135deg, #a855f7, #9333ea)' },
};

/* ── Collapsible Section ────────────────────────────────── */
const Section = ({ title, icon, children, defaultOpen = true, tooltip, contentSx, action }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <Paper
      elevation={0}
      sx={{
        borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden',
        transition: 'box-shadow 0.2s',
        '&:hover': { boxShadow: '0 2px 8px rgba(0,0,0,0.04)' },
      }}
    >
      <Box
        onClick={() => setOpen(!open)}
        sx={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          px: 2.5, py: 1.5, cursor: 'pointer',
          background: open ? 'linear-gradient(135deg, #fafafa 0%, #fff 100%)' : '#fafafa',
          borderBottom: open ? '1px solid #f3f4f6' : 'none',
          transition: 'all 0.2s',
          '&:hover': { background: '#f9fafb' },
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {icon && <Box sx={{ color: '#9ca3af', display: 'flex' }}>{icon}</Box>}
          <Typography sx={{ fontWeight: 700, fontSize: 14 }}>{title}</Typography>
          {tooltip && (
            <Tooltip title={tooltip} arrow>
              <InfoIcon sx={{ fontSize: 14, color: '#d1d5db', cursor: 'help' }} />
            </Tooltip>
          )}
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {action && <Box onClick={e => e.stopPropagation()}>{action}</Box>}
          <Box sx={{
            width: 24, height: 24, borderRadius: '50%', display: 'flex',
            alignItems: 'center', justifyContent: 'center',
            bgcolor: '#f3f4f6', transition: 'transform 0.3s',
            transform: open ? 'rotate(180deg)' : 'rotate(0deg)',
          }}>
            <ExpandMoreIcon sx={{ fontSize: 18, color: '#9ca3af' }} />
          </Box>
        </Box>
      </Box>
      <Collapse in={open} timeout={300}>
        <Box sx={{ p: 2.5, ...contentSx }}>{children}</Box>
      </Collapse>
    </Paper>
  );
};

/* ═══════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ═══════════════════════════════════════════════════════════════ */

const RiskMLDetail = () => {
  const { t } = useTranslation();
  const { ticker } = useParams();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [history, setHistory] = useState([]);
  const [days, setDays] = useState(60);
  const [showAllRows, setShowAllRows] = useState(false);

  const fetchData = useCallback(async () => {
    if (!ticker) return;
    setLoading(true);
    setError(null);
    try {
      const res = await riskMLAPI.getStockDetail(ticker.toUpperCase(), days);
      const data = (res.data || []).reverse();
      setHistory(data);
    } catch (err) {
      setError(err.response?.data?.detail || err.message || t('riskML.failedToLoad'));
    } finally {
      setLoading(false);
    }
  }, [ticker, days, t]);

  useEffect(() => { fetchData(); }, [fetchData]);

  /* ─── Derived ─────────────────────────────────────────── */
  const latest    = history.length > 0 ? history[history.length - 1] : null;
  const prev      = history.length > 1 ? history[history.length - 2] : null;
  const alertDays = history.filter(h => h.dynamic_alert).length;
  const avgProb   = history.length > 0 ? history.reduce((s, h) => s + h.ml_prob, 0) / history.length : 0;
  const sevTier   = latest?.severity_tier ?? 0;
  const sev       = SEV[sevTier] || SEV[0];
  const ensStd    = latest?.ensemble_std ?? null;

  const riskDelta = prev && latest ? ((latest.ml_prob - prev.ml_prob) * 100) : null;

  const trendDir = useMemo(() => {
    if (history.length < 3) return 'stable';
    const w = Math.min(history.length, 5);
    const recent = history.slice(-w);
    const y = recent.map(r => r.ml_prob * 100);
    const xm = (w - 1) / 2;
    const ym = y.reduce((a, b) => a + b, 0) / w;
    let num = 0, den = 0;
    for (let i = 0; i < w; i++) { num += (i - xm) * (y[i] - ym); den += (i - xm) ** 2; }
    const slope = den ? num / den : 0;
    return slope > 1 ? 'rising' : slope < -1 ? 'falling' : 'stable';
  }, [history]);

  const confidence = useMemo(() => {
    if (ensStd == null) return null;
    if (ensStd < 0.08) return { level: 'high', color: '#22c55e' };
    if (ensStd < 0.15) return { level: 'med', color: '#ea580c' };
    return { level: 'low', color: '#dc2626' };
  }, [ensStd]);

  const chartData = useMemo(() => history.map(h => ({
    date: h.date?.slice(5),
    fullDate: h.date,
    prob: +(h.ml_prob * 100).toFixed(1),
    smoothed: +(((h.smoothed_prob || h.ml_prob) * 100).toFixed(1)),
    threshold: +((h.dynamic_threshold || 0.5) * 100).toFixed(1),
    position: +(h.target_position * 100).toFixed(0),
    severity: h.severity_tier,
    alert: h.dynamic_alert,
    reEntry: h.re_entry_signal,
  })), [history]);

  /* ─── Action ──────────────────────────────────────────── */
  let actionKey, actionColor;
  if (!latest) { actionKey = 'hold'; actionColor = '#9ca3af'; }
  else if (sevTier >= 3) { actionKey = (latest.target_position || 0) < 0.3 ? 'sellAvoid' : 'reduce'; actionColor = '#dc2626'; }
  else if (sevTier >= 2) { actionKey = (latest.target_position || 0) < 0.5 ? 'reduce' : 'monitor'; actionColor = '#ef4444'; }
  else if (sevTier >= 1) { actionKey = 'watch'; actionColor = '#eab308'; }
  else if (latest.re_entry_signal) { actionKey = 'buySignal'; actionColor = '#16a34a'; }
  else { actionKey = 'hold'; actionColor = '#22c55e'; }

  /* ─── Render ──────────────────────────────────────────── */

  if (loading) {
    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', py: 12, gap: 2 }}>
        <CircularProgress size={40} thickness={5} sx={{ color: '#ef4444' }} />
        <Typography sx={{ fontSize: 13, color: '#9ca3af', fontWeight: 500 }}>
          {t('riskML.detail.title', { ticker: ticker?.toUpperCase() })}
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 6 }}>
        <Alert severity="error" sx={{ borderRadius: 1.5 }}
          action={<Chip label={t('riskML.retry')} onClick={fetchData} size="small"
            sx={{ bgcolor: '#dc2626', color: '#fff', fontWeight: 600 }} />}
        >{error}</Alert>
      </Box>
    );
  }

  if (!latest) {
    return (
      <Box sx={{ p: 3, maxWidth: 500, mx: 'auto', mt: 6 }}>
        <Alert severity="info" sx={{ borderRadius: 1.5 }}>{t('riskML.noData', 'Không có dữ liệu')}</Alert>
      </Box>
    );
  }

  const currentRisk = (latest.ml_prob * 100).toFixed(1);
  const currentPos = (latest.target_position * 100).toFixed(0);

  return (
    <Box sx={{ maxWidth: 1200, mx: 'auto', pb: 4 }}>
      {/* ═══════════════════════════════════════════════════════
          HEADER
          ═══════════════════════════════════════════════════════ */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3, px: { xs: 1.5, md: 3 }, pt: 1 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
          <Tooltip title={t('riskML.detail.back')} arrow>
            <IconButton onClick={() => navigate('/risk-dashboard')} size="small"
              sx={{ bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}>
              <BackIcon sx={{ fontSize: 20 }} />
            </IconButton>
          </Tooltip>
          <Box sx={{
            width: 42, height: 42, borderRadius: 1.5,
            background: sev.gradient,
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            boxShadow: `0 4px 14px ${sev.bg}4D`,
          }}>
            <ShieldIcon sx={{ fontSize: 22, color: '#fff' }} />
          </Box>
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography sx={{ fontSize: '1.35rem', fontWeight: 800, color: '#1e293b', letterSpacing: -0.3 }}>
                {ticker?.toUpperCase()}
              </Typography>
              <Chip label={t(`riskML.severity.${sev.key}`)} size="small" sx={{
                height: 20, fontSize: 10, fontWeight: 800, bgcolor: sev.bg, color: '#fff',
              }} />
              {trendDir !== 'stable' && (
                <Chip
                  icon={trendDir === 'rising' ? <TrendUpIcon sx={{ fontSize: 13 }} /> : <TrendDownIcon sx={{ fontSize: 13 }} />}
                  label={t(`riskML.detail.trend.${trendDir}`)}
                  size="small" variant="outlined"
                  sx={{
                    height: 20, fontSize: 10, fontWeight: 700,
                    borderColor: trendDir === 'rising' ? '#ef4444' : '#22c55e',
                    color: trendDir === 'rising' ? '#ef4444' : '#22c55e',
                    '& .MuiChip-icon': { color: 'inherit' },
                  }}
                />
              )}
              {confidence && (
                <Tooltip title={t('riskML.detail.confidenceTooltip', { level: t(`riskML.detail.confidence.${confidence.level}`) })} arrow>
                  <Chip label={t(`riskML.detail.confidence.${confidence.level}`)} size="small" sx={{
                    height: 20, fontSize: 9.5, fontWeight: 700,
                    bgcolor: `${confidence.color}14`, color: confidence.color,
                  }} />
                </Tooltip>
              )}
            </Box>
            <Typography sx={{ fontSize: '0.75rem', color: '#94a3b8', fontWeight: 500 }}>
              {t('riskML.detail.title', { ticker: ticker?.toUpperCase() })}
              {latest.date && ` · ${latest.date}`}
            </Typography>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <ToggleButtonGroup
            value={days} exclusive
            onChange={(_, v) => v && setDays(v)}
            size="small"
            sx={{ '& .MuiToggleButton-root': { px: 1.5, py: 0.5, fontSize: 12, fontWeight: 600, textTransform: 'none' } }}
          >
            {[30, 60, 120].map(d => (
              <ToggleButton key={d} value={d}>{d}d</ToggleButton>
            ))}
          </ToggleButtonGroup>
          <IconButton onClick={fetchData} size="small" sx={{ bgcolor: '#f3f4f6', '&:hover': { bgcolor: '#e5e7eb' } }}>
            <RefreshIcon sx={{ fontSize: 18 }} />
          </IconButton>
        </Box>
      </Box>

      <Box sx={{ px: { xs: 1.5, md: 3 }, display: 'flex', flexDirection: 'column', gap: 2 }}>
        {/* ═══════════════════════════════════════════════════════
            SEVERITY BANNER
            ═══════════════════════════════════════════════════════ */}
        {sevTier >= 2 && (
          <Alert
            severity={sevTier >= 3 ? 'error' : 'warning'}
            variant="filled"
            sx={{ borderRadius: 1.5, '& .MuiAlert-message': { fontSize: 13, fontWeight: 500 } }}
          >
            {t(`riskML.detail.banner.${sev.key}`, {
              ticker: ticker?.toUpperCase(),
              prob: currentRisk,
              action: t(`riskML.actions.${actionKey}`),
            })}
          </Alert>
        )}

        {/* ═══════════════════════════════════════════════════════
            HERO METRICS
            ═══════════════════════════════════════════════════════ */}
        <Paper elevation={0} sx={{
          borderRadius: 1.5, border: '1px solid #e5e7eb', overflow: 'hidden',
          background: `linear-gradient(135deg, ${sev.light} 0%, #fff 60%)`,
        }}>
          <Box sx={{
            display: 'flex', flexWrap: 'wrap',
            '& > *': { flex: '1 1 0', minWidth: 140, px: 2.5, py: 2, borderRight: '1px solid #f3f4f6' },
            '& > *:last-child': { borderRight: 'none' },
          }}>
            {/* Risk Probability */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <WarningIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.detail.currentRisk')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: sev.bg, lineHeight: 1 }}>
                  {currentRisk}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: sev.bg }}>%</Typography>
              </Box>
              {riskDelta !== null && Math.abs(riskDelta) >= 0.1 && (
                <Typography sx={{ fontSize: 11, color: riskDelta > 0 ? '#ef4444' : '#22c55e', fontWeight: 600, mt: 0.5 }}>
                  {riskDelta > 0 ? '▲' : '▼'} {riskDelta > 0 ? '+' : ''}{riskDelta.toFixed(1)}% {t('riskML.delta.vsYesterday')}
                </Typography>
              )}
            </Box>

            {/* Position Size */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <SpeedIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.detail.positionSize')}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                  {currentPos}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748b' }}>%</Typography>
              </Box>
              <Box sx={{ mt: 0.8, width: 80, height: 4, borderRadius: 2, bgcolor: '#f3f4f6', overflow: 'hidden' }}>
                <Box sx={{ height: '100%', borderRadius: 2, width: `${Math.min(currentPos, 100)}%`,
                  bgcolor: currentPos < 30 ? '#ef4444' : currentPos < 70 ? '#eab308' : '#22c55e',
                  transition: 'width 0.4s' }} />
              </Box>
            </Box>

            {/* Action */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <BoltIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.detail.actionLabel', 'Hành Động')}
                </Typography>
              </Box>
              <Chip label={t(`riskML.actions.${actionKey}`)} size="small" sx={{
                height: 26, fontSize: 12, fontWeight: 800,
                bgcolor: actionColor, color: '#fff',
                mt: 0.5,
              }} />
            </Box>

            {/* Alert Days */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <AlertIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.detail.alertDays', { d: days })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                  {alertDays}
                </Typography>
                <Typography sx={{ fontSize: 12, fontWeight: 600, color: '#9ca3af' }}>
                  / {history.length}
                </Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
                {history.length > 0 ? Math.round(alertDays / history.length * 100) : 0}%
              </Typography>
            </Box>

            {/* Average */}
            <Box>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: 0.8 }}>
                <BarChartIcon sx={{ fontSize: 14, color: '#9ca3af' }} />
                <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#9ca3af', textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  {t('riskML.detail.avgRisk', { d: days })}
                </Typography>
              </Box>
              <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
                <Typography sx={{ fontSize: 28, fontWeight: 800, color: '#1e293b', lineHeight: 1 }}>
                  {(avgProb * 100).toFixed(1)}
                </Typography>
                <Typography sx={{ fontSize: 14, fontWeight: 700, color: '#64748b' }}>%</Typography>
              </Box>
              <Typography sx={{ fontSize: 11, color: '#9ca3af', mt: 0.5 }}>
                {t('riskML.detail.avgRiskSub', { max: ((Math.max(...history.map(h => h.ml_prob)) || 0) * 100).toFixed(1) })}
              </Typography>
            </Box>
          </Box>
        </Paper>

        {/* ═══════════════════════════════════════════════════════
            CHART — Risk + Position dual overlay
            ═══════════════════════════════════════════════════════ */}
        <Section
          title={t('riskML.detail.riskProbOverTime')}
          icon={<ChartIcon sx={{ fontSize: 16 }} />}
          tooltip={t('riskML.tooltips.riskPct')}
        >
          <ResponsiveContainer width="100%" height={300}>
            <ComposedChart data={chartData}>
              <defs>
                <linearGradient id="gradRisk" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#ef4444" stopOpacity={0.3} />
                  <stop offset="95%" stopColor="#ef4444" stopOpacity={0.02} />
                </linearGradient>
                <linearGradient id="gradPos" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#22c55e" stopOpacity={0.15} />
                  <stop offset="95%" stopColor="#22c55e" stopOpacity={0.02} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
              <XAxis dataKey="date" fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={{ stroke: '#e5e7eb' }} tickLine={false} />
              <YAxis yAxisId="left" domain={[0, 100]} fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={false} tickLine={false} />
              <YAxis yAxisId="right" domain={[0, 100]} orientation="right" fontSize={10} tick={{ fill: '#9ca3af' }} axisLine={false} tickLine={false} hide />
              <RTooltip content={({ active, payload }) => {
                if (!active || !payload?.length) return null;
                const d = payload[0]?.payload;
                const s = SEV[d?.severity || 0];
                return (
                  <Paper elevation={0} sx={{ px: 1.5, py: 1, borderRadius: 1.5, border: '1px solid #e5e7eb',
                    borderLeft: `3px solid ${s.bg}`, boxShadow: '0 4px 12px rgba(0,0,0,0.08)' }}>
                    <Typography sx={{ fontSize: 12, fontWeight: 700, mb: 0.5 }}>{d?.fullDate}</Typography>
                    <Box sx={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '2px 10px' }}>
                      <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{t('riskML.detail.tooltipRisk')}:</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 700, color: s.bg }}>{d?.prob}%</Typography>
                      <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{t('riskML.detail.tooltipSmoothed')}:</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600 }}>{d?.smoothed}%</Typography>
                      <Typography sx={{ fontSize: 11, color: '#9ca3af' }}>{t('riskML.detail.tooltipPosition')}:</Typography>
                      <Typography sx={{ fontSize: 11, fontWeight: 600, color: '#22c55e' }}>{d?.position}%</Typography>
                    </Box>
                    <Chip size="small" label={t(`riskML.severity.${s.key}`)}
                      sx={{ mt: 0.5, height: 16, fontSize: 9, fontWeight: 700,
                        bgcolor: `${s.bg}1A`, color: s.bg }} />
                  </Paper>
                );
              }} />
              <ReferenceLine yAxisId="left" y={20} stroke="#22c55e" strokeDasharray="4 4" strokeOpacity={0.4} />
              <ReferenceLine yAxisId="left" y={60} stroke="#a855f7" strokeDasharray="4 4" strokeOpacity={0.4} />
              <Area yAxisId="left" type="monotone" dataKey="prob" name={t('riskML.detail.riskPct')}
                stroke="#ef4444" strokeWidth={2} fill="url(#gradRisk)" />
              <Line yAxisId="left" type="monotone" dataKey="smoothed" name={t('riskML.detail.smoothed')}
                stroke="#ea580c" strokeWidth={1.5} dot={false} strokeDasharray="4 2" />
              <Line yAxisId="left" type="stepAfter" dataKey="threshold" name={t('riskML.detail.threshold')}
                stroke="#9ca3af" strokeWidth={1} dot={false} strokeDasharray="6 3" />
              <Area yAxisId="right" type="monotone" dataKey="position" name={t('riskML.detail.positionPct')}
                stroke="#22c55e" strokeWidth={1.5} fill="url(#gradPos)" strokeDasharray="3 2" />
            </ComposedChart>
          </ResponsiveContainer>
        </Section>

        {/* ═══════════════════════════════════════════════════════
            PREDICTION HISTORY TABLE
            ═══════════════════════════════════════════════════════ */}
        <Section
          title={t('riskML.detail.predictionHistory')}
          icon={<CalendarIcon sx={{ fontSize: 16 }} />}
          contentSx={{ p: 0 }}
          action={history.length > 10 && (
            <Button size="small" onClick={() => setShowAllRows(p => !p)}
              sx={{ fontSize: 11, fontWeight: 600, textTransform: 'none', borderRadius: 1, color: '#6b7280' }}>
              {showAllRows ? t('riskML.detail.collapse', 'Thu gọn') : t('riskML.detail.showAll', 'Xem tất cả ({{count}})').replace('{{count}}', history.length)}
            </Button>
          )}
        >
          <TableContainer sx={{ maxHeight: 420 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow sx={{
                  background: 'linear-gradient(180deg, #fef2f2 0%, #fff1f2 100%)',
                  '& th': {
                    fontWeight: 700, fontSize: 11, color: '#b91c1c', py: 1,
                    borderBottom: '2px solid #fecaca', whiteSpace: 'nowrap',
                    bgcolor: '#fef2f2',
                  },
                }}>
                  <TableCell>{t('riskML.detail.tableDate')}</TableCell>
                  <TableCell align="right">{t('riskML.detail.tableRisk')}</TableCell>
                  <TableCell align="center">{t('riskML.detail.tableSeverity')}</TableCell>
                  <TableCell align="right">{t('riskML.detail.tablePosition')}</TableCell>
                  <TableCell align="center">{t('riskML.detail.tableAlert')}</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {(() => {
                  const reversed = [...history].reverse();
                  const rows = showAllRows ? reversed : reversed.slice(0, 10);
                  return rows.map((h, idx) => {
                    const s = SEV[h.severity_tier] || SEV[0];
                    const isLatest = idx === 0;
                    return (
                      <TableRow
                        key={h.date}
                        sx={{
                          background: isLatest
                            ? `linear-gradient(90deg, ${s.light} 0%, #fff 40%)`
                            : h.dynamic_alert
                              ? 'linear-gradient(90deg, #fef2f2 0%, #fff 30%)'
                              : 'transparent',
                          '& td': { borderBottom: '1px solid #f3f4f6', py: 1 },
                          '& td:first-of-type': { borderLeft: `3px solid ${s.bg}` },
                          '&:hover': { bgcolor: '#f9fafb' },
                          transition: 'background 0.15s',
                        }}
                      >
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                            <Typography sx={{ fontWeight: isLatest ? 700 : 600, fontSize: 12, color: isLatest ? '#1e293b' : '#6b7280' }}>
                              {h.date}
                            </Typography>
                            {isLatest && (
                              <Chip label={t('riskML.detail.latest', 'Mới nhất')} size="small" sx={{
                                height: 16, fontSize: 9, fontWeight: 700,
                                bgcolor: sev.bg, color: '#fff',
                                '& .MuiChip-label': { px: 0.5 },
                              }} />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.8, justifyContent: 'flex-end' }}>
                            <Typography sx={{ fontWeight: 700, fontSize: 13, color: s.bg }}>
                              {(h.ml_prob * 100).toFixed(1)}%
                            </Typography>
                            <Box sx={{ width: 40, height: 4, borderRadius: 2, overflow: 'hidden', bgcolor: '#f3f4f6' }}>
                              <Box sx={{
                                height: '100%', borderRadius: 2,
                                width: `${Math.min(h.ml_prob * 100, 100)}%`,
                                bgcolor: s.bg,
                              }} />
                            </Box>
                          </Box>
                        </TableCell>
                        <TableCell align="center">
                          <Chip label={t(`riskML.severity.${s.key}`)} size="small" sx={{
                            height: 18, fontSize: 10, fontWeight: 800,
                            bgcolor: s.bg, color: '#fff',
                            '& .MuiChip-label': { px: 0.6 },
                          }} />
                        </TableCell>
                        <TableCell align="right">
                          <Typography sx={{
                            fontWeight: 600, fontSize: 12,
                            color: h.target_position < 0.3 ? '#ef4444' : h.target_position < 0.7 ? '#ea580c' : '#22c55e',
                          }}>
                            {(h.target_position * 100).toFixed(0)}%
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                            {h.dynamic_alert && (
                              <Chip label={t('riskML.signals.alert')} size="small" sx={{
                                height: 16, fontSize: 9, fontWeight: 700,
                                bgcolor: '#ef4444', color: '#fff',
                                '& .MuiChip-label': { px: 0.5 },
                              }} />
                            )}
                            {h.re_entry_signal && (
                              <Chip label={t('riskML.signals.reEntry')} size="small" sx={{
                                height: 16, fontSize: 9, fontWeight: 700,
                                bgcolor: '#22c55e', color: '#fff',
                                '& .MuiChip-label': { px: 0.5 },
                              }} />
                            )}
                            {!h.dynamic_alert && !h.re_entry_signal && (
                              <Typography sx={{ fontSize: 11, color: '#d1d5db' }}>—</Typography>
                            )}
                          </Box>
                        </TableCell>
                      </TableRow>
                    );
                  });
                })()}
              </TableBody>
            </Table>
          </TableContainer>
        </Section>
      </Box>
    </Box>
  );
};

export default RiskMLDetail;
